/**
 * Subscription Store (Zustand)
 * Manages subscription state globally
 * Works with both native (iOS/Android) and web platforms
 */

import { create } from 'zustand';
import { AppState, type NativeEventSubscription } from 'react-native';
import {
  initializePurchases,
  loginUser,
  logoutUser,
  getCustomerInfo,
  getOfferings,
  purchasePackage,
  restorePurchases,
  parseSubscriptionStatus,
  isPurchasesConfigured,
  presentPaywall,
  type PurchasesOffering,
  type PurchasesPackage,
} from '../services/purchases';
import { UserProfileCacheStorage } from '../generated/sync/sync-config';
import { SubscriptionInfo } from '../types';
import { createLogger } from '../utils/logger';
import { E2E_MODE } from '../config/constants';
import i18n from '../i18n';

const log = createLogger('SubscriptionStore');

// ── E2E entitlement override ─────────────────────────────────────────────────
// Set only by `applyFakeEntitlement` below, whose only caller is the
// `__DEV__`-stripped `dev.set-entitlement` handler. While non-null, no real
// source may write `subscription` — see `setGuarded`.
let _entitlementOverride: SubscriptionInfo | null = null;

/**
 * Apply a state patch, dropping its `subscription` field while an E2E override
 * is active.
 *
 * Every real source of entitlement — the RevenueCat SDK read on login and on
 * refresh, the profile-cache fallback, a completed purchase, a restore — writes
 * through here. Without the guard an injected state would survive only until
 * the next `fetchSubscription`, which fires on the auth cascade and on the
 * 24 h foreground TTL, and every story asserting on a paywall would be racing
 * it. Suppressing the source is what makes the injected state deterministic.
 *
 * Everything else in the patch (`isLoading`, `error`, …) is applied normally:
 * the real code path still runs and still reports what it did, it just cannot
 * move entitlement out from under the test.
 */
function setGuarded(
  set: (patch: Partial<SubscriptionState>) => void,
  patch: Partial<SubscriptionState>,
): void {
  if (_entitlementOverride !== null && 'subscription' in patch) {
    const { subscription: _suppressed, ...rest } = patch;
    if (Object.keys(rest).length > 0) set(rest);
    return;
  }
  set(patch);
}

/** Extract user-facing message from RevenueCat SDK errors (not our API). */
function purchaseErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return i18n.t('errors.api.UNKNOWN');
}

/** TTL safety net: refetch subscription on foreground if older than 24h */
const SUBSCRIPTION_TTL_MS = 24 * 60 * 60 * 1000;
let _lastFetchedAt = 0;
let _appStateSubscription: NativeEventSubscription | null = null;

function startSubscriptionForegroundCheck(): void {
  if (_appStateSubscription) return;
  _appStateSubscription = AppState.addEventListener('change', (state) => {
    if (state === 'active' && _lastFetchedAt > 0 && Date.now() - _lastFetchedAt > SUBSCRIPTION_TTL_MS) {
      useSubscriptionStore.getState().fetchSubscription().catch(() => {});
    }
  });
}

interface SubscriptionState {
  // State
  subscription: SubscriptionInfo | null;
  offerings: PurchasesOffering | null;
  isLoading: boolean;
  isPurchasing: boolean;
  error: string | null;

  // Actions
  initialize: () => Promise<void>;
  loginWithUserId: (userId: string) => Promise<void>;
  logout: () => Promise<void>;
  fetchSubscription: () => Promise<void>;
  fetchOfferings: () => Promise<void>;
  purchase: (pkg: PurchasesPackage) => Promise<boolean>;
  presentPaywall: () => Promise<boolean>;
  restore: () => Promise<boolean>;
  clearError: () => void;
}

export const useSubscriptionStore = create<SubscriptionState>((set, get) => ({
  subscription: null,
  offerings: null,
  isLoading: false,
  isPurchasing: false,
  error: null,

  /**
   * Initialize RevenueCat SDK
   */
  initialize: async () => {
    try {
      await initializePurchases();
    } catch (error: unknown) {
      log.error('Failed to initialize purchases', error);
    }
  },

  /**
   * Login user to RevenueCat after authentication
   */
  loginWithUserId: async (userId: string) => {
    set({ isLoading: true, error: null });

    try {
      // Login to RevenueCat with authenticated user ID
      const customerInfo = await loginUser(userId);
      const status = parseSubscriptionStatus(customerInfo);

      setGuarded(set, {
        subscription: {
          plan: status.plan,
          status: status.isActive ? 'active' : 'free',
          isActive: status.isActive,
          expiresAt: status.expiresAt?.toISOString(),
        },
        isLoading: false,
      });

      // Fetch offerings in background
      get().fetchOfferings().catch((err) => log.warn('Failed to fetch offerings', { error: err.message }));
    } catch (error: unknown) {
      log.error('RevenueCat login failed', error);
      // On error, try to fetch from backend as fallback
      try {
        await get().fetchSubscription();
      } catch {
        set({
          isLoading: false,
          error: purchaseErrorMessage(error),
        });
      }
    }
  },

  /**
   * Logout from RevenueCat
   */
  logout: async () => {
    try {
      await logoutUser();
      // Signing out drops an E2E override too. A lingering one would leave a
      // signed-out user holding a non-null `subscription`, a state the app
      // never produces on its own — and a story that signs out and back in
      // would silently inherit the previous session's entitlement.
      _entitlementOverride = null;
      set({
        subscription: null,
        offerings: null,
        error: null,
      });
    } catch (error: unknown) {
      log.error('Subscription logout failed', error);
    }
  },

  /**
   * Fetch current subscription status
   */
  fetchSubscription: async () => {
    set({ isLoading: true, error: null });

    try {
      if (isPurchasesConfigured()) {
        // Fetch from RevenueCat
        const customerInfo = await getCustomerInfo();
        const status = parseSubscriptionStatus(customerInfo);

        setGuarded(set, {
          subscription: {
            plan: status.plan,
            status: status.isActive ? 'active' : 'free',
            isActive: status.isActive,
            expiresAt: status.expiresAt?.toISOString(),
          },
          isLoading: false,
        });
      } else {
        // Read from profile cache (populated by fetchProfile) instead of a separate API call
        const cached = await UserProfileCacheStorage.load();
        if (cached?.subscription) {
          const sub = cached.subscription;
          // `source` is what separates a trial from a purchase — both carry
          // `hasAccess: true`. Branching on access alone told a trialling
          // learner they were Pro, with a Manage button for a subscription
          // they did not have, and left `useIsInTrial()` permanently false so
          // the trial badge and days-remaining copy could never render (A22).
          const isTrial = sub.source === 'trial';
          setGuarded(set, {
            subscription: {
              plan: isTrial ? 'trial' : sub.hasAccess ? 'pro' : 'free',
              status: isTrial ? 'trial' : sub.hasAccess ? 'active' : 'free',
              isActive: sub.hasAccess,
              expiresAt: sub.expiresAt ?? undefined,
              // Server-computed: the client has no trial end date to count
              // from. Absent on a non-trial, and absent when this client is
              // talking to a backend that predates the field — the badge
              // renders without a day count then, rather than showing "0".
              ...(isTrial && typeof sub.trialDaysLeft === 'number'
                ? { trialDaysLeft: sub.trialDaysLeft }
                : {}),
            },
            isLoading: false,
          });
        } else {
          setGuarded(set, {
            subscription: {
              plan: 'free',
              status: 'free',
              isActive: false,
            },
            isLoading: false,
          });
        }
      }

      // Track last fetch time for TTL safety-net (foreground check)
      _lastFetchedAt = Date.now();
      startSubscriptionForegroundCheck();
    } catch (error: unknown) {
      log.error('Subscription fetch failed', error);
      set({
        isLoading: false,
        error: purchaseErrorMessage(error),
      });
    }
  },

  /**
   * Fetch available offerings (products)
   */
  fetchOfferings: async () => {
    try {
      const offerings = await getOfferings();
      set({ offerings });
    } catch (error: unknown) {
      log.error('Fetch offerings failed', error);
    }
  },

  /**
   * Purchase a subscription package
   */
  purchase: async (pkg: PurchasesPackage) => {
    set({ isPurchasing: true, error: null });

    try {
      const customerInfo = await purchasePackage(pkg);
      const status = parseSubscriptionStatus(customerInfo);

      setGuarded(set, {
        subscription: {
          plan: status.plan,
          status: status.isActive ? 'active' : 'free',
          isActive: status.isActive,
          expiresAt: status.expiresAt?.toISOString(),
        },
        isPurchasing: false,
      });

      return status.isActive;
    } catch (error: unknown) {
      if (error instanceof Error && error.message === 'CANCELLED') {
        set({ isPurchasing: false });
        return false;
      }

      log.error('Purchase failed', error);
      set({
        isPurchasing: false,
        error: purchaseErrorMessage(error),
      });
      return false;
    }
  },

  /**
   * Present RevenueCat Paywall UI
   * This is the recommended way to show subscription options
   */
  presentPaywall: async () => {
    set({ isPurchasing: true, error: null });

    try {
      const success = await presentPaywall();

      if (success) {
        // Refresh subscription status after successful purchase
        await get().fetchSubscription();
      }

      set({ isPurchasing: false });
      return success;
    } catch (error: unknown) {
      log.error('Paywall presentation failed', error);
      set({
        isPurchasing: false,
        error: purchaseErrorMessage(error),
      });
      return false;
    }
  },

  /**
   * Restore previous purchases
   */
  restore: async () => {
    set({ isLoading: true, error: null });

    try {
      const customerInfo = await restorePurchases();
      const status = parseSubscriptionStatus(customerInfo);

      setGuarded(set, {
        subscription: {
          plan: status.plan,
          status: status.isActive ? 'active' : 'free',
          isActive: status.isActive,
          expiresAt: status.expiresAt?.toISOString(),
        },
        isLoading: false,
      });

      return status.isActive;
    } catch (error: unknown) {
      log.error('Restore purchases failed', error);
      set({
        isLoading: false,
        error: purchaseErrorMessage(error),
      });
      return false;
    }
  },

  /**
   * Clear error message
   */
  clearError: () => {
    set({ error: null });
  },
}));

/**
 * E2E seam: declare the entitlement the app should believe it has.
 *
 * What a purchase costs to test for real is a device, a signed build, a store
 * sandbox account and a human at the payment sheet — none of which an agent or
 * CI has. But *what a paid user sees* needs none of that: it needs the store to
 * hold a state, which is what this sets. It is the difference between "the
 * purchase works" (still a manual device round-trip, H1b on the board) and
 * "the paywall, the trial badge and the gated screens render correctly for each
 * state", which was untested entirely.
 *
 * Passing `null` clears the override and hands entitlement back to the real
 * sources; the next `fetchSubscription` repopulates it.
 *
 * **Inert outside E2E.** Returns false and changes nothing unless `E2E_MODE` is
 * on, so a production build cannot be talked into granting itself pro. The only
 * caller is the `__DEV__`-stripped `dev.set-entitlement` handler, and the
 * runner-side intent turns a `false` return into an explicit failure rather
 * than letting a story assert against an app that never changed state.
 *
 * @returns whether the override was applied (false ⇒ not in E2E_MODE).
 */
export function applyFakeEntitlement(next: SubscriptionInfo | null): boolean {
  if (!E2E_MODE) return false;
  log.info('Applying faked entitlement (E2E)', { next });
  _entitlementOverride = next;
  useSubscriptionStore.setState({
    subscription: next,
    isLoading: false,
    isPurchasing: false,
    error: null,
  });
  return true;
}

/** Whether an E2E entitlement override is currently suppressing real writes. */
export function hasEntitlementOverride(): boolean {
  return _entitlementOverride !== null;
}

/**
 * Helper hook to check if user has pro access
 */
export function useHasProAccess(): boolean {
  const subscription = useSubscriptionStore((state) => state.subscription);
  return subscription?.isActive ?? false;
}

/**
 * Helper hook to check if user is in trial
 */
export function useIsInTrial(): boolean {
  const subscription = useSubscriptionStore((state) => state.subscription);
  return subscription?.status === 'trial';
}

/**
 * Helper hook to get trial days left
 */
export function useTrialDaysLeft(): number | null {
  const subscription = useSubscriptionStore((state) => state.subscription);
  return subscription?.trialDaysLeft ?? null;
}
